import { trackPageView } from '@snowplow/browser-tracker';
import {
    ButtonClick,
    FileDownload,
    FormComplete,
    FormError,
    FormStart,
    FormSubmit,
    FormView,
    LinkClick,
    ListFilterEventSchema,
    Page,
    Share,
    TabSelect,
    trackButtonClickSpec,
    trackFileDownloadSpec,
    trackFormCompleteSpec,
    trackFormErrorSpec,
    trackFormStartSpec,
    trackFormSubmitSpec,
    trackFormViewSpec,
    trackLinkClickSpec,
    trackListFilterSpec,
    trackShareSpec,
    trackTabSelectSpec,
} from '../types/snowtype/snowplow';
import { ITrackingFnGtm, ITrackingFnSnowplow } from '../models';
import {
    ITrackingEventGtm,
    ITrackingEventConfigGtm,
    ITrackingEventSnowplow,
} from '../models/tracking/tracking-event.model';

export abstract class TrackingEvents {
    private static Snowplow = class {
        static formView = (x: FormView): ITrackingEventSnowplow => {
            return { fn: trackFormViewSpec, data: x };
        };

        static formStart = (x: FormStart): ITrackingEventSnowplow => {
            return { fn: trackFormStartSpec, data: x };
        };

        static formComplete = (x: FormComplete): ITrackingEventSnowplow => {
            return { fn: trackFormCompleteSpec, data: x };
        };

        static formSubmit = (x: FormSubmit): ITrackingEventSnowplow => {
            return { fn: trackFormSubmitSpec, data: x };
        };

        static formError = (x: FormError): ITrackingEventSnowplow => {
            return { fn: trackFormErrorSpec, data: x };
        };

        static buttonClick = (x: ButtonClick): ITrackingEventSnowplow => {
            return { fn: trackButtonClickSpec, data: x };
        };

        static share = (x: Share): ITrackingEventSnowplow => {
            return { fn: trackShareSpec, data: x };
        };

        static fileDownload = (x: FileDownload): ITrackingEventSnowplow => {
            return { fn: trackFileDownloadSpec, data: x };
        };

        static linkClick = (x: LinkClick): ITrackingEventSnowplow => {
            return { fn: trackLinkClickSpec, data: x };
        };

        static tabSelect = (x: TabSelect): ITrackingEventSnowplow => {
            return { fn: trackTabSelectSpec, data: x };
        };

        static listFilter = (x: ListFilterEventSchema): ITrackingEventSnowplow => {
            return { fn: trackListFilterSpec, data: x };
        };

        static pageView = (x: Page): ITrackingEventSnowplow => {
            return { fn: trackPageView, data: x };
        };
    };

    private static Gtm = class {
        static buttonClick: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'button_click',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static pageView: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'page_view',
                models: [{ type: 'pageModel', obj: objs[0] }],
            });
        };

        static listView: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'item_list_view',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static listItemSelect: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'item_select',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static itemView: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'item_view',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static listFilter: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'item_list_filter',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static formView: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'form_view',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static formError: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'form_error',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static formSubmit: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'form_submit',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static formStart: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'form_start',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static formComplete: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'form_complete',
                models: [
                    { type: 'eventModel', obj: objs[0] },
                    { type: 'userModel', obj: objs[1] },
                ],
            });
        };

        static carouselSelect: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'carousel_select_slide',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static carouselNext: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'carousel_next_slide',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static carouselPrevious: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'carousel_previous_slide',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static tabSelect: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'tab_select',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static share: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'share',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static clipboardCopy: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'clipboard_copy',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static fileDownload: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'file_download',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static footerClick: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'footer_click',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static topMenuClick: ITrackingFnGtm = (objs: unknown[]) => {
            return TrackingEvents.Gtm.mergeModels({
                name: 'top_menu_click',
                models: [{ type: 'eventModel', obj: objs[0] }],
            });
        };

        static mergeModels(e: ITrackingEventConfigGtm) {
            const obj: ITrackingEventGtm = {};
            obj['event'] = e.name;

            e.models.forEach((x) => {
                obj[x.type] = x.obj as { [key: string]: unknown };
            });

            return obj;
        }
    };

    static gtm: { [key: string]: ITrackingFnGtm } = {
        buttonClick: TrackingEvents.Gtm.buttonClick,
        pageView: TrackingEvents.Gtm.pageView,
        listView: TrackingEvents.Gtm.listView,
        listItemSelect: TrackingEvents.Gtm.listItemSelect,
        itemView: TrackingEvents.Gtm.itemView,
        listFilter: TrackingEvents.Gtm.listFilter,
        formView: TrackingEvents.Gtm.formView,
        formError: TrackingEvents.Gtm.formError,
        formSubmit: TrackingEvents.Gtm.formSubmit,
        formStart: TrackingEvents.Gtm.formStart,
        formComplete: TrackingEvents.Gtm.formComplete,
        carouselSelect: TrackingEvents.Gtm.carouselSelect,
        carouselNext: TrackingEvents.Gtm.carouselNext,
        carouselPrevious: TrackingEvents.Gtm.carouselPrevious,
        tabSelect: TrackingEvents.Gtm.tabSelect,
        share: TrackingEvents.Gtm.share,
        clipboardCopy: TrackingEvents.Gtm.clipboardCopy,
        fileDownload: TrackingEvents.Gtm.fileDownload,
        footerClick: TrackingEvents.Gtm.footerClick,
        topMenuClick: TrackingEvents.Gtm.topMenuClick,
    };

    static snowplow: {
        [key: string]: ITrackingFnSnowplow;
    } = {
        formView: TrackingEvents.Snowplow.formView,
        formStart: TrackingEvents.Snowplow.formStart,
        formSubmit: TrackingEvents.Snowplow.formSubmit,
        formError: TrackingEvents.Snowplow.formError,
        formComplete: TrackingEvents.Snowplow.formComplete,
        buttonClick: TrackingEvents.Snowplow.buttonClick,
        share: TrackingEvents.Snowplow.share,
        fileDownload: TrackingEvents.Snowplow.fileDownload,
        linkClick: TrackingEvents.Snowplow.linkClick,
        tabSelect: TrackingEvents.Snowplow.tabSelect,
        listFilter: TrackingEvents.Snowplow.listFilter,
        pageView: TrackingEvents.Snowplow.pageView,
    };
}
